/**
 * Hook para gestionar el modulo de Compliance y Auditoria
 * Proporciona funciones para audit log, alertas y metricas
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import type {
  AuditLogRecord,
  ComplianceAlert,
  ComplianceMetrics,
  IntegrityCheckResult,
  ActionCategory,
  SensitivityLevel,
  AlertStatus,
  AlertSeverity,
} from '@/types/compliance';

const AUDIT_LOG_KEY = 'audit-log';
const ALERTS_KEY = 'compliance-alerts';
const METRICS_KEY = 'compliance-metrics';

// ==================== AUDIT LOG ====================

interface UseAuditLogOptions {
  entityType?: string;
  entityId?: string;
  actionCategory?: ActionCategory;
  performedBy?: string;
  sensitivityLevel?: SensitivityLevel;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  limit?: number;
}

export function useAuditLog(options: UseAuditLogOptions = {}) {
  const {
    entityType,
    entityId,
    actionCategory,
    performedBy,
    sensitivityLevel,
    dateFrom,
    dateTo,
    search,
    limit = 100,
  } = options;

  return useQuery({
    queryKey: [AUDIT_LOG_KEY, options],
    queryFn: async () => {
      let query = api
        .from('audit_log_immutable')
        .select('*')
        .order('timestamp_utc', { ascending: false })
        .limit(limit);

      if (entityType) {
        query = query.eq('entity_type', entityType);
      }
      if (entityId) {
        query = query.eq('entity_id', entityId);
      }
      if (actionCategory) {
        query = query.eq('action_category', actionCategory);
      }
      if (performedBy) {
        query = query.eq('performed_by', performedBy);
      }
      if (sensitivityLevel) {
        query = query.eq('sensitivity_level', sensitivityLevel);
      }
      if (dateFrom) {
        query = query.gte('timestamp_utc', dateFrom);
      }
      if (dateTo) {
        query = query.lte('timestamp_utc', dateTo);
      }
      if (search) {
        query = query.or(`change_summary.ilike.%${search}%,entity_type.ilike.%${search}%,performed_by_name.ilike.%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as AuditLogRecord[];
    },
  });
}

export function useAuditLogById(id: string | null) {
  return useQuery({
    queryKey: [AUDIT_LOG_KEY, 'detail', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api
        .from('audit_log_immutable')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as AuditLogRecord;
    },
    enabled: !!id,
  });
}

export function useAuditLogInsert() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      entity_type: string;
      entity_id: string;
      action: string;
      action_category: ActionCategory;
      previous_state?: Record<string, any>;
      new_state?: Record<string, any>;
      change_summary?: string;
      performed_by?: string;
      performed_by_role?: string;
      performed_by_name?: string;
      sensitivity_level?: SensitivityLevel;
      compliance_tags?: string[];
    }) => {
      const { data, error } = await api.rpc('audit_log_insert', {
        p_entity_type: params.entity_type,
        p_entity_id: params.entity_id,
        p_action: params.action,
        p_action_category: params.action_category,
        p_previous_state: params.previous_state || null,
        p_new_state: params.new_state || null,
        p_change_summary: params.change_summary || null,
        p_performed_by: params.performed_by || null,
        p_performed_by_role: params.performed_by_role || 'system',
        p_performed_by_name: params.performed_by_name || 'Sistema',
        p_sensitivity_level: params.sensitivity_level || 'normal',
        p_compliance_tags: params.compliance_tags || [],
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AUDIT_LOG_KEY] });
      queryClient.invalidateQueries({ queryKey: [METRICS_KEY] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ==================== INTEGRIDAD ====================

export function useVerifyIntegrity() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params?: { from_sequence?: number; to_sequence?: number }) => {
      const { data, error } = await api.rpc('verify_audit_chain_integrity', {
        p_from_sequence: params?.from_sequence || 1,
        p_to_sequence: params?.to_sequence || null,
      });
      if (error) throw error;
      return data as IntegrityCheckResult[];
    },
    onSuccess: (data) => {
      const result = data[0];
      if (result.is_valid) {
        toast({
          title: 'Integridad verificada',
          description: `${result.checked_records} registros verificados correctamente`,
        });
      } else {
        toast({
          title: 'Error de integridad',
          description: result.error_details || 'Cadena de auditoria comprometida',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ==================== METRICAS ====================

export function useComplianceMetrics(days: number = 30) {
  return useQuery({
    queryKey: [METRICS_KEY, days],
    queryFn: async () => {
      const { data, error } = await api.rpc('get_compliance_metrics', {
        p_days: days,
      });
      if (error) throw error;
      return data as ComplianceMetrics;
    },
    refetchInterval: 60000, // Refrescar cada minuto
  });
}

// ==================== ALERTAS ====================

interface UseAlertsOptions {
  status?: AlertStatus | 'all';
  severity?: AlertSeverity | 'all';
  assignedTo?: string;
  limit?: number;
}

export function useComplianceAlerts(options: UseAlertsOptions = {}) {
  const { status, severity, assignedTo, limit = 50 } = options;

  return useQuery({
    queryKey: [ALERTS_KEY, options],
    queryFn: async () => {
      let query = api
        .from('compliance_alerts')
        .select('*')
        .order('detected_at', { ascending: false })
        .limit(limit);

      if (status && status !== 'all') {
        query = query.eq('status', status);
      }
      if (severity && severity !== 'all') {
        query = query.eq('severity', severity);
      }
      if (assignedTo) {
        query = query.eq('assigned_to', assignedTo);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as ComplianceAlert[];
    },
    refetchInterval: 30000, // Refrescar cada 30 segundos
  });
}

export function useAlertById(id: string | null) {
  return useQuery({
    queryKey: [ALERTS_KEY, 'detail', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api
        .from('compliance_alerts')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as ComplianceAlert;
    },
    enabled: !!id,
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { id: string; userId: string }) => {
      const { data, error } = await api
        .from('compliance_alerts')
        .update({
          status: 'acknowledged',
          acknowledged_by: params.userId,
          acknowledged_at: new Date().toISOString(),
        })
        .eq('id', params.id)
        .select()
        .single();
      if (error) throw error;
      return data as ComplianceAlert;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ALERTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [METRICS_KEY] });
      toast({
        title: 'Alerta reconocida',
        description: 'La alerta ha sido marcada como reconocida',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useResolveAlert() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      userId: string;
      resolution_notes: string;
      status?: 'resolved' | 'false_positive';
    }) => {
      const { data, error } = await api
        .from('compliance_alerts')
        .update({
          status: params.status || 'resolved',
          resolved_by: params.userId,
          resolved_at: new Date().toISOString(),
          resolution_notes: params.resolution_notes,
        })
        .eq('id', params.id)
        .select()
        .single();
      if (error) throw error;
      return data as ComplianceAlert;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ALERTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [METRICS_KEY] });
      toast({
        title: 'Alerta resuelta',
        description: 'La alerta ha sido resuelta correctamente',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ==================== ESTADISTICAS RAPIDAS ====================

export function useAuditStats() {
  return useQuery({
    queryKey: [AUDIT_LOG_KEY, 'stats'],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data, error } = await api
        .from('audit_log_immutable')
        .select('id, action_category, sensitivity_level', { count: 'exact' })
        .gte('timestamp_utc', today.toISOString());

      if (error) throw error;

      const stats = {
        total_today: data?.length || 0,
        high_risk_today: data?.filter(r => r.sensitivity_level === 'high' || r.sensitivity_level === 'critical').length || 0,
      };

      return stats;
    },
    refetchInterval: 30000,
  });
}

export function useOpenAlertsCount() {
  return useQuery({
    queryKey: [ALERTS_KEY, 'open-count'],
    queryFn: async () => {
      const { count, error } = await api
        .from('compliance_alerts')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'open');
      if (error) throw error;
      return count || 0;
    },
    refetchInterval: 30000,
  });
}

// ==================== FIRMAS ELECTRONICAS ====================

const SIGNATURES_KEY = 'electronic-signatures';

export interface ElectronicSignature {
  id: string;
  document_type: string;
  document_id: string;
  document_hash?: string;
  signer_id?: string;
  signer_name: string;
  signer_role: string;
  signer_email?: string;
  signature_type: 'simple' | 'advanced' | 'qualified';
  signature_level: 'basic' | 'substantial' | 'high';
  verification_code: string;
  signed_at: string;
  is_valid: boolean;
  revoked_at?: string;
  revocation_reason?: string;
}

export function useSignatures(documentType?: string, documentId?: string) {
  return useQuery({
    queryKey: [SIGNATURES_KEY, documentType, documentId],
    queryFn: async () => {
      let query = api
        .from('electronic_signatures')
        .select('*')
        .order('signed_at', { ascending: false });

      if (documentType) {
        query = query.eq('document_type', documentType);
      }
      if (documentId) {
        query = query.eq('document_id', documentId);
      }

      const { data, error } = await query.limit(100);
      if (error) throw error;
      return (data || []) as ElectronicSignature[];
    },
  });
}

export function useRecentSignatures(days: number = 30) {
  return useQuery({
    queryKey: [SIGNATURES_KEY, 'recent', days],
    queryFn: async () => {
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - days);

      const { data, error } = await api
        .from('electronic_signatures')
        .select('*')
        .gte('signed_at', fromDate.toISOString())
        .order('signed_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data || []) as ElectronicSignature[];
    },
    refetchInterval: 60000,
  });
}

export function useVerifySignature() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (verificationCode: string) => {
      const { data, error } = await api.rpc('verify_signature', {
        p_verification_code: verificationCode.toUpperCase(),
      });
      if (error) throw error;
      return data as ElectronicSignature[] | null;
    },
    onError: (error: Error) => {
      toast({
        title: 'Error de verificacion',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRevokeSignature() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { signatureId: string; reason: string; revokedBy: string }) => {
      const { data, error } = await api.rpc('revoke_signature', {
        p_signature_id: params.signatureId,
        p_reason: params.reason,
        p_revoked_by: params.revokedBy,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SIGNATURES_KEY] });
      queryClient.invalidateQueries({ queryKey: [ALERTS_KEY] });
      toast({
        title: 'Firma revocada',
        description: 'La firma ha sido revocada correctamente',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useSignaturesCount() {
  return useQuery({
    queryKey: [SIGNATURES_KEY, 'count'],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data, error } = await api
        .from('electronic_signatures')
        .select('id, is_valid')
        .gte('signed_at', today.toISOString());

      if (error) throw error;

      return {
        total_today: data?.length || 0,
        valid: data?.filter(s => s.is_valid).length || 0,
        revoked: data?.filter(s => !s.is_valid).length || 0,
      };
    },
    refetchInterval: 60000,
  });
}

// ==================== APROBACIONES PENDIENTES ====================

const APPROVALS_KEY = 'pending-approvals';

export interface PendingApproval {
  id: string;
  entity_type: string;
  entity_id: string;
  policy_id?: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  requested_by: string;
  requested_at: string;
  request_justification?: string;
  required_approvers: number;
  current_approvals: number;
  approval_chain: Array<{
    user_id: string;
    name: string;
    role: string;
    approved_at: string;
    notes?: string;
  }>;
  final_status?: string;
  resolved_at?: string;
  resolved_by?: string;
  resolution_notes?: string;
  expires_at?: string;
  requester_name?: string;
  policy_name?: string;
}

export function usePendingApprovals(status?: 'pending' | 'all') {
  return useQuery({
    queryKey: [APPROVALS_KEY, status],
    queryFn: async () => {
      let query = api
        .from('pending_approvals_view')
        .select('*')
        .order('requested_at', { ascending: false });

      if (status && status !== 'all') {
        query = query.eq('status', status);
      }

      const { data, error } = await query.limit(50);
      if (error) throw error;
      return (data || []) as PendingApproval[];
    },
    refetchInterval: 30000,
  });
}

export function useRequestApproval() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      entityType: string;
      entityId: string;
      requestedBy: string;
      justification?: string;
      priority?: 'low' | 'normal' | 'high' | 'urgent';
    }) => {
      const { data, error } = await api.rpc('request_approval', {
        p_entity_type: params.entityType,
        p_entity_id: params.entityId,
        p_requested_by: params.requestedBy,
        p_justification: params.justification || null,
        p_priority: params.priority || 'normal',
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APPROVALS_KEY] });
      toast({
        title: 'Solicitud creada',
        description: 'La solicitud de aprobacion ha sido creada',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useApproveRequest() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { approvalId: string; approverId: string; notes?: string }) => {
      const { data, error } = await api.rpc('approve_request', {
        p_approval_id: params.approvalId,
        p_approver_id: params.approverId,
        p_notes: params.notes || null,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APPROVALS_KEY] });
      queryClient.invalidateQueries({ queryKey: [AUDIT_LOG_KEY] });
      toast({
        title: 'Solicitud aprobada',
        description: 'La aprobacion ha sido registrada',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRejectRequest() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { approvalId: string; rejectorId: string; reason: string }) => {
      const { data, error } = await api.rpc('reject_request', {
        p_approval_id: params.approvalId,
        p_rejector_id: params.rejectorId,
        p_reason: params.reason,
      });
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APPROVALS_KEY] });
      queryClient.invalidateQueries({ queryKey: [ALERTS_KEY] });
      toast({
        title: 'Solicitud rechazada',
        description: 'La solicitud ha sido rechazada',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function usePendingApprovalsCount() {
  return useQuery({
    queryKey: [APPROVALS_KEY, 'count'],
    queryFn: async () => {
      const { count, error } = await api
        .from('pending_approvals')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (error) throw error;
      return count || 0;
    },
    refetchInterval: 30000,
  });
}
